import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  AutoResponses,
  TwoWaySetupService,
} from '@app/two-way/services/two-way-setup.service';
import { TitleService } from '@app/core/services/title.service';
import { ProductGroup } from '@app/core/models/client';
import { TwoWayConfig } from '@app/core/models/two-way-config';
import { User } from '@app/core/models/user';
import { NewFeedTransitionService } from '@app/core/services/new-feed-transition.service';
import { SessionService } from '@app/security/session.service';
import { Client } from '@app/core/models/client';
import { FeatureService } from '@app/core/services/feature.service';
import { FeatureFlags } from '@app/core/models/feature-flags';
import { SetupBannerAutoresponseComponent } from '../setup-ooo-banner-autoresponse/setup-banner-autoresponse.component';
import { SpinnerComponent } from '../../../../shared/spinner/spinner.component';
import { NewGenericPhoneComponent } from '../../../../shared/new-generic-phone/new-generic-phone.component';
import { WireHeaderComponent } from '../../../../shared/wire-header/wire-header.component';
import { TwoWayPreviewComponent } from '../two-way-preview/two-way-preview.component';
import { TwoWayPreviewReactContainerComponent } from '../../../../shared/phone/two-way-preview-react-container/two-way-preview-react-container.component';
import { BubbleComponent } from '../../../../shared/bubble/bubble.component';
import { SmsWithDisabledFlag } from '../setup-sms-ping/setup-sms-ping.component';
import { SmsService } from '@app/core/services/sms.service';

export type AutoResponseMessages = {
  id: string;
  message: string;
  title: string;
};
@Component({
  selector: 'app-setup-summary',
  templateUrl: './setup-summary.component.html',
  styleUrls: ['./setup-summary.component.scss'],
  standalone: true,
  imports: [
    SetupBannerAutoresponseComponent,
    SpinnerComponent,
    NewGenericPhoneComponent,
    WireHeaderComponent,
    TwoWayPreviewComponent,
    TwoWayPreviewReactContainerComponent,
    BubbleComponent,
  ],
})
export class SetupSummaryComponent implements OnInit, OnDestroy {
  twoWayConfig: TwoWayConfig;
  twoWayUsers: User[];
  defaultProductGroup: ProductGroup;
  currentClient: Client;
  isRMAutoResponseEnabled: boolean;
  isFeedEnabled: boolean;
  autoResponseMessages: AutoResponseMessages[];
  isErrorAutoResponse: boolean;
  isErrorPingMessages: boolean;
  clientId: string;
  featureFlags = FeatureFlags;
  pingSmsText: string;

  constructor(
    private titleService: TitleService,
    private twoWaySetupService: TwoWaySetupService,
    public newFeed: NewFeedTransitionService,
    private sessionService: SessionService,
    public featureService: FeatureService,
    private smsService: SmsService,
  ) { }
  get headerColor() {
    return this.defaultProductGroup && this.defaultProductGroup?.branding?.color
      ? this.defaultProductGroup.branding.color
      : '#231F20';
  }
  ngOnInit() {
    this.isRMAutoResponseEnabled = this.featureService.checkFlag(
      FeatureFlags.rm_auto_response,
    );
    this.isFeedEnabled = this.newFeed.enabled;
    this.currentClient = this.sessionService.getCurrentUsersClient();
    this.clientId = this.currentClient.id;
    this.titleService.activate(
      this.isFeedEnabled ? 'Relay Messenger Setup' : 'Two Way Messaging Setup',
      'Summary',
    );
    this.twoWaySetupService.getTwoWayConfig().subscribe((twoWayConfig) => {
      this.twoWayConfig = twoWayConfig;
      this.trySmsServiceInit();
      this.tryLoadPingMessages();
    });

    this.twoWaySetupService
      .getDefaultProductGroup()
      .subscribe((defaultProductGroup) => {
        this.defaultProductGroup = defaultProductGroup;
        this.trySmsServiceInit();
        this.tryLoadPingMessages();
      });

    if (this.isRMAutoResponseEnabled && this.isFeedEnabled) {
      this.twoWaySetupService
        .getAutoResponses(this.currentClient.id)
        .subscribe({
          next: (autoResponses) => {
            this.isErrorAutoResponse = false;
            this.autoResponseMessages =
              this.convertAutoResponses(autoResponses);
          },
          error: () => {
            this.isErrorAutoResponse = true;
          },
        });
    }
  }

  private trySmsServiceInit(): void {
    if (this.twoWayConfig && this.defaultProductGroup) {
      this.smsService.init(this.twoWayConfig.sms, this.defaultProductGroup);
    }
  }

  private tryLoadPingMessages(): void {
    if (
      !this.twoWayConfig ||
      !this.defaultProductGroup ||
      !this.isRMPingTranslationsEnabled ||
      !this.legacySms.ping_sms_disabled
    ) {
      return;
    }
    this.twoWaySetupService
      .getPingMessagesWithLanguages(this.currentClient.id, 'en')
      .subscribe({
        next: (res) => {
          this.isErrorPingMessages = false;
          this.pingSmsText = `${res.pingMessages.notificationText} ${this.smsService.getMessageURL()} ${res.pingMessages.disclaimer}`;
        },
        error: () => {
          this.isErrorPingMessages = true;
        },
      });
  }

  /**
 * Determines if the "RM Ping Translations" is enabled for the user
 */
  get isRMPingTranslationsEnabled(): boolean {
    return this.featureService.checkFlag(this.featureFlags.rm_ping_translations);
  }

  private get legacySms(): SmsWithDisabledFlag {
    return this.twoWayConfig.sms as SmsWithDisabledFlag;
  }

  get showUpdatedPingText(): boolean {
    return this.isRMPingTranslationsEnabled && !!this.legacySms?.ping_sms_disabled;
  }

  convertAutoResponses(autoResponses: AutoResponses): AutoResponseMessages[] {
    const autoResponseTitles: { [key: string]: string } = {
      businessHours: 'Welcome',
      afterHours: 'After Hours',
      outOfOffice: 'Out of Office',
    };
    return Object.keys(autoResponses).map((key) => ({
      id: key,
      message: autoResponses[key as keyof AutoResponses].message,
      title: autoResponseTitles[key],
    }));
  }

  ngOnDestroy() {
    this.titleService.deactivate();
  }

  isLoading() {
    return this.twoWayConfig === undefined;
  }

  get rmTranslationsWithLanguagesFlagOn(): boolean {
    return this.featureService.checkFlag(FeatureFlags.rm_multi_lingual);
  }

  get isTranslationsEnabled(): boolean {
    return (
      this.rmTranslationsWithLanguagesFlagOn &&
      !!this.currentClient?.two_way_config?.translations_enabled
    );
  }
}
